import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  getApplianceTariff,
  getApplianceThings,
  setApplianceTariff,
  setThingNominal,
} from '../../../../services';
import { notifyError, notifySuccess } from '../../../../utils';
import * as s from './styled-plant-params';

// Modal de PARAMETROS ANALITICOS (restrito a role privilegiado): cadastra a
// tarifa da usina (override) e a potencia nominal (placa) de cada maquina —
// dados que alimentam os KPIs de Financeiro e Fator de capacidade.
// A tarifa padrao do cliente e exibida so como referencia (editada na tela
// do cliente). PATCHs sao restritos tambem no backend (assertHighPrivilege).

// Converte input -> numero ou null (campo vazio remove o valor).
const parseNum = (str) => {
  const t = (str || '').trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined; // undefined = invalido
};

const PlantParams = ({ isOpen, onClose, applianceId, things = [], onSaved }) => {
  const [applianceTariffInput, setApplianceTariffInput] = useState('');
  const [customerTariff, setCustomerTariff] = useState(null);
  const [nominalInputs, setNominalInputs] = useState({}); // thingId -> string
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || applianceId == null) return undefined;
    let active = true;
    setLoading(true);
    Promise.all([
      getApplianceTariff(applianceId).catch(() => null),
      getApplianceThings(applianceId).catch(() => null),
    ]).then(([tariffRes, thingsRes]) => {
      if (!active) return;
      const t = (tariffRes && tariffRes.data) || tariffRes || {};
      setApplianceTariffInput(
        t.applianceTariff != null ? String(t.applianceTariff) : '',
      );
      setCustomerTariff(t.customerTariff != null ? Number(t.customerTariff) : null);
      const appliance = (thingsRes && thingsRes.data) || thingsRes || {};
      const list = appliance.thing || appliance.things || [];
      const map = {};
      list.forEach((th) => {
        map[th.id] = th.potenciaNominal != null ? String(th.potenciaNominal) : '';
      });
      setNominalInputs(map);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [isOpen, applianceId]);

  if (!isOpen) return null;

  const handleSave = async () => {
    const tariffVal = parseNum(applianceTariffInput);
    if (tariffVal === undefined) {
      notifyError('Tarifa inválida.');
      return;
    }
    setSaving(true);
    const ops = [setApplianceTariff(applianceId, tariffVal)];
    things.forEach((th) => {
      const raw = nominalInputs[th.id];
      const val = parseNum(raw);
      if (val === undefined) return; // ignora invalido individual
      ops.push(setThingNominal(th.id, val));
    });
    try {
      await Promise.all(ops);
      notifySuccess('Parâmetros salvos.');
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      notifyError('Não foi possível salvar os parâmetros.');
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <s.Backdrop
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <s.Panel role="dialog" aria-modal="true" aria-label="Parâmetros analíticos">
        <s.Header>
          <s.Title>Parâmetros analíticos</s.Title>
          <s.Close type="button" onClick={onClose} aria-label="Fechar">
            ×
          </s.Close>
        </s.Header>

        {loading ? (
          <s.Body>
            <s.Hint>Carregando…</s.Hint>
          </s.Body>
        ) : (
          <s.Body>
            <s.Field>
              <s.Label>Tarifa da usina (R$/kWh)</s.Label>
              <s.Input
                type="text"
                inputMode="decimal"
                placeholder={
                  customerTariff != null
                    ? `padrão do cliente: ${customerTariff}`
                    : 'ex.: 0,75'
                }
                value={applianceTariffInput}
                onChange={(e) => setApplianceTariffInput(e.target.value)}
              />
              <s.Hint>
                Vazio = usa a tarifa padrão do cliente
                {customerTariff != null ? ` (R$ ${customerTariff}/kWh)` : ''}.
              </s.Hint>
            </s.Field>

            {things.length > 0 && (
              <s.Group>
                <s.GroupLabel>Potência nominal — placa (kW)</s.GroupLabel>
                {things.map((th, idx) => (
                  <s.MachineRow key={th.id || idx}>
                    <s.MachineName title={th.name || th.thing}>
                      {th.name || th.thing || `Máquina ${idx + 1}`}
                    </s.MachineName>
                    <s.Input
                      $small
                      type="text"
                      inputMode="decimal"
                      placeholder="kW"
                      value={nominalInputs[th.id] || ''}
                      onChange={(e) =>
                        setNominalInputs((prev) => ({
                          ...prev,
                          [th.id]: e.target.value,
                        }))
                      }
                    />
                  </s.MachineRow>
                ))}
              </s.Group>
            )}
          </s.Body>
        )}

        <s.Footer>
          <s.Secondary type="button" onClick={onClose} disabled={saving}>
            Cancelar
          </s.Secondary>
          <s.Primary type="button" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Salvando…' : 'Salvar'}
          </s.Primary>
        </s.Footer>
      </s.Panel>
    </s.Backdrop>,
    document.body,
  );
};

export default PlantParams;
